import { Trans } from 'react-i18next';
import { i18n } from '/business/shared/translations/i18n';

import type { QuestionTreeItem } from '/business/shared/functions/types';

import { questionScopeCategories } from '/business/shared/util/Statics';

import StyledParagraph from '/shared/styled/components/StyledParagraph';

import Scope1Icon from '/business/shared/images/icons/bp-icon-car-building-bp-green.svg';
import Scope3Icon from '/business/shared/images/icons/bp-icon-diagram-distribution-bp-green.svg';
import Scope2Icon from '/business/shared/images/icons/bp-icon-electricity-bolt-bp-green.svg';

type Props = {
  questions?: QuestionTreeItem[];
};

export const questionBuilder = ({ questions = [] }: Props): QuestionTreeItem[] => {
  const categoryGroups: QuestionTreeItem[] = [
    {
      id: 'scope-1',
      scopeCategory: questionScopeCategories.SCOPE_1,
      questions: questions.filter((question) => question.scopeCategory === questionScopeCategories.SCOPE_1),
      enlargeIcon: true,
      title: i18n.t('business:emissionScopes.scope1.title'),
      reductionPageBaseTitle: i18n.t('business:ReducePage.reductions.groups.scope1.title'),
      description: (
        <>
          <p>
            {/* @i18n: These are direct carbon emissions from sources that are owned or controlled by your business. */}
            <Trans i18nKey="business:emissionScopes.scope1.description.intro" components={{ 1: <b /> }} />
          </p>
          <ul>
            {questions
              .filter((question) => question.scopeCategory === 'scope1')
              .map((question) => (
                <li key={`${question.id}-scope1`}>
                  <Trans i18nKey={question.overviewPageTitleI18nKey} />
                </li>
              ))}
          </ul>
        </>
      ),
      groupIntro: (
        <p>
          {/* @i18n: These are direct carbon emissions from sources that are owned or controlled by the[...] */}
          <Trans i18nKey="business:ReducePage.reductions.groups.scope1.intro" components={{ 1: <b /> }} />
        </p>
      ),
      icon: Scope1Icon,
      cta: {
        title: i18n.t('business:emissionScopes.scope1.buttonCta'),
      },
      form: import('/business/shared/components/questions/scope1/Scope1SelectorForm'),
    },
    {
      id: 'scope-2',
      scopeCategory: questionScopeCategories.SCOPE_2,
      questions: questions.filter((question) => question.scopeCategory === questionScopeCategories.SCOPE_2),
      groupIntro: (
        <p>
          {/* @i18n: These are indirect carbon emissions from the generation of purchased electricity[...] */}
          <Trans i18nKey="business:ReducePage.reductions.groups.scope2.intro" components={{ 1: <b /> }} />
        </p>
      ),
      enlargeIcon: true,
      title: i18n.t('business:emissionScopes.scope2.title'),
      reductionPageBaseTitle: i18n.t('business:ReducePage.reductions.groups.scope2.title'),
      description: (
        <>
          <p>
            {/* @i18n: These are indirect carbon emissions from the generation of purchased electricity[...] */}
            <Trans i18nKey="business:emissionScopes.scope2.description.intro" components={{ 1: <b /> }} />
          </p>
          <ul>
            {questions
              .filter((question) => question.scopeCategory === 'scope2')
              .map((question) => (
                <li key={`${question.id}-scope2`}>
                  <Trans i18nKey={question.overviewPageTitleI18nKey} />
                </li>
              ))}
          </ul>
        </>
      ),
      icon: Scope2Icon,
      cta: {
        title: i18n.t('business:emissionScopes.scope2.buttonCta'),
      },
      form: import('/business/shared/components/questions/scope2/Scope2SelectorForm'),
    },
    {
      id: 'scope-3',
      scopeCategory: questionScopeCategories.SCOPE_3,
      questions: questions.filter((question) => question.scopeCategory === questionScopeCategories.SCOPE_3),
      groupIntro: (
        <p>
          {/* @i18n: These are your calculated indirect carbon emissions that occur in the business's value[...] */}
          <Trans
            i18nKey="business:ReducePage.reductions.groups.scope3.intro"
            components={{ 1: <b />, 3: <br />, 4: <br /> }}
          />
        </p>
      ),
      enlargeIcon: true,
      title: i18n.t('business:emissionScopes.scope3.title'),
      breadcrumbTitle: i18n.t('business:emissionScopes.scope3.title'),
      titleReducePage: i18n.t('business:emissionScopes.scope3.title'),
      reductionPageBaseTitle: i18n.t('business:ReducePage.reductions.groups.scope3.title'),
      description: (
        <>
          <p>
            {/* @i18n: These are indirect carbon emissions that occur in your business’s value chain. */}
            <Trans i18nKey="business:emissionScopes.scope3.description.intro" components={{ 1: <b /> }} />
          </p>
          <ul>
            {questions
              .filter((question) => question.scopeCategory === 'scope3')
              .map((question) => (
                <li key={`${question.id}-scope3`}>
                  <Trans i18nKey={question.overviewPageTitleI18nKey} />
                </li>
              ))}
          </ul>
          <StyledParagraph $bold $extraSmallText $hasExtraMarginTop>
            {/* @i18n: *Please note the Carbon Management Portal calculations do not cover all scope 3[...] */}
            <Trans i18nKey="business:emissionScopes.scope3.description.footNote" />
          </StyledParagraph>
        </>
      ),
      icon: Scope3Icon,
      cta: {
        title: i18n.t('business:emissionScopes.scope3.buttonCta'),
      },
      form: import('/business/shared/components/questions/scope3/Scope3SelectorForm'),
    },
  ];

  return [
    {
      id: 'calculation',
      title: i18n.t('business:Questions.calculations.title'),
      helper: i18n.t('business:Questions.calculations.helper'),
      subTitle: i18n.t('business:Questions.calculations.subTitle'),
      hasUnlockStep2Button: true,
      groups: categoryGroups.filter(({ questions }) => questions?.length),
    },
  ];
};
